import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment';
import ServerSideList from '../../../commonComponents/lists/ServerSideList';
import translate from '../../../commonComponents/translate';
import {
  formatNumbers,
  newFormatPrice,
} from '../../../services/utilityFunctions';
import ActionsColumnCommission from './ActionsColumnCommission';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import {
  formatDecimalPrices,
  isSmallerThanTwoDecimal,
  normalizeNumber,
} from '../../../services/numberUtilis';
import FormInput from '../../../commonComponents/forms/FormInput';
import { SALE_STATUS } from '../proptype/commissionProp';
import WalletProp, { WALLET_TYPE } from '../proptype/walletProp';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import CSRFContext from '../../../contextProviders/CSRFContext';
import useAsyncExport from '../../../commonComponents/hooks/useAsyncExport';

const TYPE = {
  MARKET_ORDER: 'market_order', // Commission Client Order
  MARKET_ORDER_EXTRA: 'market_order_extra', // Extra Commission Client Order
  SPONSORED_USER: 'sponsored_user', // Team Commission
  MANAGER: 'manager', // Manager Commission
  PERSONAL_BONUS: 'personal_bonus', // Personal Bonus
  MARKET_ORDER_VA: 'market_order_va', // Personal Bonus
  STAR_PRIME: 'star_prime', // personal Bonus of productivity
  UNIVERSE_PRIME: 'universe_prime', // personal Bonus of productivity
  GALAXY_PRIME: 'galaxy_prime', // personal Bonus of productivity
  ENTRY_FEE: 'entry_fee', // for neofin
  BINARY_COMMISSION: 'binary_commission', // binary tree commission
  CAPPING_BINARY_COMMISSION: 'capping_binary_commission', // capping
  FAST_START_BONUS: 'fast_start_bonus', // fast_start_bonus
  VA_LEFT: 'va_left',
  VA_RIGHT: 'va_right',
};

const getCommissionName = (commissionTypes, type) =>
  commissionTypes.find(c => c.type === type)?.name || null;

// BEGIN: COLUMNS HELPER
const renderType = (t, commissionTypes, type) => {
  const commissionType = getCommissionName(commissionTypes, type);
  switch (type) {
    case TYPE.MARKET_ORDER:
      return commissionType || t('commissions.myCommissions.market_order');
    case TYPE.MARKET_ORDER_EXTRA:
      return (
        commissionType || t('commissions.myCommissions.market_order_extra')
      );
    case TYPE.SPONSORED_USER:
      return commissionType || t('commissions.myCommissions.sponsored_user');
    case TYPE.MANAGER:
      return commissionType || t('commissions.myCommissions.manager');
    case TYPE.PERSONAL_BONUS:
      return commissionType || t('commissions.myCommissions.personal_bonus');
    case TYPE.STAR_PRIME:
      return commissionType || t('commissions.myCommissions.star_prime');
    case TYPE.UNIVERSE_PRIME:
      return commissionType || t('commissions.myCommissions.universe_prime');
    case TYPE.GALAXY_PRIME:
      return commissionType || t('commissions.myCommissions.galaxy_prime');
    case TYPE.MARKET_ORDER_VA:
      return commissionType || t('commissions.userCommissions.market_order_va');
    case TYPE.ENTRY_FEE:
      return commissionType || t('commissions.userCommissions.entry_fee');
    case TYPE.BINARY_COMMISSION:
      return (
        commissionType || t('commissions.userCommissions.binary_commission')
      );
    case TYPE.CAPPING_BINARY_COMMISSION:
      return (
        commissionType ||
        t('commissions.userCommissions.capping_binary_commission')
      );
    case TYPE.FAST_START_BONUS:
      return (
        commissionType || t('commissions.userCommissions.fast_start_bonus')
      );
    case TYPE.VA_LEFT:
      return commissionType || t('commissions.vaLeft');
    case TYPE.VA_RIGHT:
      return commissionType || t('commissions.vaRight');
    default:
      return commissionType || type;
  }
};

const renderUser = (t, item, referral = true) => {
  let user = null;

  if (item) {
    if (
      referral &&
      item.user &&
      (item.type === TYPE.SPONSORED_USER || item.type === TYPE.ENTRY_FEE)
    ) {
      user = item?.user;
    }
    if (!referral && item.sourceUser) {
      user = item?.sourceUser;
    }
  }
  return renderToStaticMarkup(
    <>{user ? <UserColumn t={t} user={user} /> : '-'}</>,
  );
};

const renderOrderPrice = (amount, currency, type, hide = false) => {
  if (hide) {
    return '-';
  }
  switch (type) {
    case TYPE.MARKET_ORDER:
    case TYPE.MARKET_ORDER_VA:
      return isSmallerThanTwoDecimal(amount)
        ? formatDecimalPrices(amount, true, currency)
        : newFormatPrice(amount, true, currency, true);
    case TYPE.MARKET_ORDER_EXTRA:
    case TYPE.MANAGER:
    case TYPE.STAR_PRIME:
    case TYPE.UNIVERSE_PRIME:
    case TYPE.GALAXY_PRIME:
      return '-';
    case TYPE.PERSONAL_BONUS:
    case TYPE.SPONSORED_USER:
      if (typeof amount === 'number' && amount) {
        return isSmallerThanTwoDecimal(amount)
          ? formatDecimalPrices(amount, true, currency)
          : newFormatPrice(amount, true, currency, true);
      }
      return '-';
    default:
      return isSmallerThanTwoDecimal(amount)
        ? formatDecimalPrices(amount, true, currency)
        : newFormatPrice(amount, true, currency, true);
  }
};

const renderOrderVA = (VA, type) => {
  switch (type) {
    case TYPE.MARKET_ORDER:
    case TYPE.MARKET_ORDER_VA:
    case TYPE.SPONSORED_USER:
      return VA ? normalizeNumber(VA, 2) : '-';
    case TYPE.MARKET_ORDER_EXTRA:
    case TYPE.MANAGER:
    case TYPE.PERSONAL_BONUS:
    case TYPE.STAR_PRIME:
    case TYPE.UNIVERSE_PRIME:
    case TYPE.GALAXY_PRIME:
    case TYPE.ENTRY_FEE:
      return '-';
    default:
      return VA ? normalizeNumber(VA, 2) : '-';
  }
};

const renderPercentage = (percentage, type) => {
  switch (type) {
    case TYPE.STAR_PRIME:
    case TYPE.UNIVERSE_PRIME:
    case TYPE.GALAXY_PRIME:
      return '-';
    default:
      return percentage && percentage >= 0 ? `${percentage.toFixed(2)}%` : '-';
  }
};

const renderCommissionVA = (commissionVA, type) => {
  switch (type) {
    case TYPE.STAR_PRIME:
    case TYPE.UNIVERSE_PRIME:
    case TYPE.GALAXY_PRIME:
    case TYPE.ENTRY_FEE:
      return '-';
    default:
      return commissionVA ? normalizeNumber(commissionVA, 2) : '-';
  }
};

const renderSales = (t, sale) => {
  const { status } = sale || {};
  let statusTranslated;
  let statusBadge;
  switch (status) {
    case SALE_STATUS.PAID:
      statusTranslated = t('commissions.user.sale.status.paid');
      statusBadge = 'sale-bg-paid';
      break;
    case SALE_STATUS.ON_GOING:
      statusTranslated = t('commissions.user.sale.status.on_going');
      statusBadge = 'sale-bg-ongoing';
      break;
    case SALE_STATUS.RECEIVED:
      statusTranslated = t('commissions.user.sale.status.received');
      statusBadge = 'sale-bg-received';
      break;
    case SALE_STATUS.CANCELED:
      statusTranslated = t('commissions.user.sale.status.canceled');
      statusBadge = 'sale-bg-canceled';
      break;
    case SALE_STATUS.REJECTED:
      statusTranslated = t('commissions.user.sale.status.rejected');
      statusBadge = 'sale-bg-rejected';
      break;
    case SALE_STATUS.DISABLED:
      statusTranslated = t('commissions.user.sale.status.disabled');
      statusBadge = 'sale-bg-disabled';
      break;
    default:
      statusTranslated = '-';
      statusBadge = '';
      break;
  }
  return <div className={`sale-bg ${statusBadge}`}>{statusTranslated}</div>;
};
// END: COLUMNS HELPER

// BEGIN: SUMMARY HELPER
const summaryContent = (isCrypto, currency, title, value, type, tax) => {
  let styleClass = '';
  let titleToUse = title;
  if (type === 'totalCommissions') {
    styleClass += 'bold';
  }
  if (type === 'tax' && tax) {
    titleToUse = `${title} (${tax}%)`;
  }
  return (
    <div className="row col-lg-6 col-12">
      <p className={`col-6 ${styleClass}`}>{titleToUse}</p>
      <span className={`col-4 ${styleClass}`}>
        {isCrypto
          ? `${value || 0}${currency}`
          : newFormatPrice(value, true, currency || 'eur', true)}
      </span>
    </div>
  );
};
// END: SUMMARY HELPER

function SponsorCommissionList(props) {
  const {
    t,
    onCommissionReport,
    isRecurrenceInterval,
    onListInitialization,
    tableId,
    filters,
    isWeelodgeCommissionModel,
    summary,
    currency: loggedUserCurrency,
    directSellingSettings,
    onFilterChange,
    activeWallet,
  } = props;
  const { VAIntegration: settingsVAIntegration, salesIntegration } =
    directSellingSettings;
  const { organization } = useContext(ConfigsContext);
  const {
    show,
    error,
    revenue,
    netRevenue,
    revenueTax,
    tax,
    revenueWithVAT,
    userPrices,
  } = summary;

  const isCrypto = activeWallet && activeWallet.type === WALLET_TYPE.CRYPTO;

  const _csrf = useContext(CSRFContext);
  const exportPath = isCrypto
    ? '/analytics/market-place/crypto/commissions'
    : '/analytics/market-place/commissions';
  const { startExport, isExporting } = useAsyncExport({
    t,
    csrf: _csrf,
    enqueueUrl: `/jobs${exportPath}/xlsx`,
    statusUrl: jobId => `/jobs${exportPath}/job/${jobId}/status`,
    downloadUrl: jobId => `/excel-jobs${exportPath}/job/${jobId}/download`,
    loaderContainerId: 'sponsorCommissionsExportLoader',
  });

  const VAIntegration = !isCrypto && settingsVAIntegration;

  const cryptoCurrency =
    activeWallet?.currencyInfo?.symbol_native ||
    activeWallet?.currencyInfo?.symbol;

  const formatCrypto = value => `${value || 0}${cryptoCurrency}`;

  const formatFiat = (value, currency) =>
    isSmallerThanTwoDecimal(value || 0)
      ? `${formatDecimalPrices(value || 0, true, currency)}`
      : newFormatPrice(value || 0, true, currency, true);

  const formatMoney = (value, currency) =>
    isCrypto ? formatCrypto(value) : formatFiat(value, currency);

  const columns = [
    {
      field: 'user',
      title: t('commissions.userCommissions.user'),
      textAlign: 'left',
      width: 250,
      sortable: false,
      template: item => renderUser(t, item),
    },
  ];

  columns.push({
    field: 'sourceUser',
    title: salesIntegration
      ? t('commissions.userCommissions.saleUser')
      : t('commissions.userCommissions.orderBuyer'),
    textAlign: 'left',
    width: 250,
    sortable: false,
    template: item => renderUser(t, item, false),
  });

  columns.push({
    field: 'type',
    textAlign: 'center',
    title: t('commissions.userCommissions.type'),
    template: item =>
      renderToStaticMarkup(
        <span className={`type type-${item.type}`}>
          {renderType(t, organization?.commissionTypes, item.type)}
        </span>,
      ),
  });

  if (salesIntegration) {
    columns.push({
      field: 'sales',
      textAlign: 'center',
      title: t('status'),
      template: ({ sale }) => renderToStaticMarkup(renderSales(t, sale)),
    });
  }

  columns.push({
    field: 'amount',
    textAlign: 'center',
    title: !salesIntegration
      ? t('commissions.userCommissions.amount')
      : t('commissions.userCommissions.turnover'),
    template: ({ amount, type, userPrices: ups, origin }) =>
      renderToStaticMarkup(
        <span className="no-wrap-value">
          {isCrypto
            ? formatCrypto(amount)
            : renderOrderPrice(
                ups?.amount?.price,
                ups?.amount?.code,
                type,
                origin === 'custom',
              )}
        </span>,
      ),
  });

  if (VAIntegration && !salesIntegration) {
    columns.push({
      field: 'VA',
      textAlign: 'center',
      title: t('commissions.userCommissions.order_va'),
      template: ({ VA, type }) =>
        renderToStaticMarkup(
          <span className="no-wrap-value">{renderOrderVA(VA, type)}</span>,
        ),
    });
  }

  columns.push({
    field: 'percentage',
    textAlign: 'center',
    title: t('commissions.userCommissions.percentage'),
    template: ({ percentage, type }) =>
      renderToStaticMarkup(
        <span className="no-wrap-value">
          {renderPercentage(percentage, type)}
        </span>,
      ),
  });

  if (VAIntegration) {
    columns.push({
      field: 'commissionVA',
      textAlign: 'center',
      title: t('commissions.userCommissions.va'),
      template: ({ commissionVA, type }) =>
        renderToStaticMarkup(
          <span className="no-wrap-value">
            {renderCommissionVA(commissionVA, type)}
          </span>,
        ),
    });
  }

  columns.push(
    {
      field: 'commission',
      textAlign: 'center',
      title: t('commissions.userCommissions.commission'),
      template: ({ userPrices: ups, commission, currency }) => {
        const showFormatMoney = !isCrypto ? ups?.commission?.code : commission;
        return renderToStaticMarkup(
          <span className="no-wrap-value">
            {showFormatMoney
              ? formatMoney(
                  !isCrypto ? ups?.commission?.price : commission,
                  !isCrypto ? ups?.commission?.code : currency,
                )
              : '-'}
          </span>,
        );
      },
    },
    {
      field: 'date',
      textAlign: 'center',
      title: t('commissions.userCommissions.date'),
      template: ({ date }) =>
        renderToStaticMarkup(
          <span>{moment(date).utc().format('DD MMM, YYYY')}</span>,
        ),
    },
  );

  // The actions should show if the commissionModel is weelodge or if salesIntegration is ON
  columns.push({
    field: 'Actions',
    width: 80,
    textAlign: 'center',
    title: t('actions'),
    sortable: false,
    overflow: 'visible',
    template: item =>
      renderToStaticMarkup(
        <ActionsColumnCommission
          t={t}
          commission={item}
          isWeelodgeCommissionModel={isWeelodgeCommissionModel}
          salesIntegration={salesIntegration}
        />,
      ),
  });

  const saleStatus = useMemo(
    () => [
      {
        id: 'on_going',
        text: t('commissions.user.sale.status.on_going'),
      },
      {
        id: 'received',
        text: t('commissions.user.sale.status.received'),
      },
      {
        id: 'paid',
        text: t('commissions.user.sale.status.paid'),
      },
      {
        id: 'disabled',
        text: t('commissions.user.sale.status.disabled'),
      },
      {
        id: 'rejected',
        text: t('commissions.user.sale.status.rejected'),
      },
    ],
    [],
  );

  const getCommissionListExcel = useCallback(() => {
    const payload = {
      populate: ['sale', 'user', 'sourceUser'],
    };
    if (isCrypto && activeWallet?.currencyInfo?.code) {
      payload.currency = activeWallet.currencyInfo.code;
    }
    if (filters.sponsor) payload.sponsor = filters.sponsor;
    if (filters.startDate) payload.startDate = filters.startDate;
    if (filters.endDate) payload.endDate = filters.endDate;
    if (filters.saleStatus) payload.saleStatus = filters.saleStatus;
    startExport(payload);
  }, [activeWallet, filters, isCrypto, startExport]);

  return (
    <div className="row">
      {isExporting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div id="sponsorCommissionsExportLoader" />
        </div>
      )}
      <div className="col-lg-12 col-12 my-commission-list">
        <div className="commissions-section-header">
          <div className="commissions-section-title">
            {t('header_myCommissions')}
          </div>
          <div className="commission-list-tools">
            {isRecurrenceInterval && (
              <button
                type="button"
                className="btn btn-sm btn-pdf-export"
                onClick={onCommissionReport}
              >
                {t('commissions.export.commissionsReport')}
              </button>
            )}
            <button
              type="button"
              onClick={() => getCommissionListExcel()}
              disabled={isExporting}
              className="btn btn-excel-export btn-sm"
            >
              {t('export.excel')}
            </button>
          </div>
        </div>
        <div>
          {salesIntegration ? (
            <FormInput
              type="select"
              onChange={onFilterChange('saleStatus')}
              id="saleStatus"
              placeholder={t('status')}
              value={filters.saleStatus || ''}
              containerClassName="col-lg-3 col-12 px-0"
              inputProps={{
                items: saleStatus,
                allowClear: true,
              }}
            />
          ) : null}
          <ServerSideList
            columns={columns}
            id={tableId}
            dataSourceURL={
              activeWallet?.type === WALLET_TYPE.CRYPTO
                ? `/table/analytics/market-place/crypto/commissions/list?currency=${activeWallet?.currencyInfo?.code}`
                : '/table/analytics/market-place/commissions/list'
            }
            dataSourceHeaders={{
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            }}
            dataSourceParams={{ populate: ['sourceUser', 'user', 'sale'] }}
            onInit={onListInitialization}
            query={filters}
          />
          {show && (
            <div className="row summary-report-commissions">
              {summaryContent(
                isCrypto,
                isCrypto ? cryptoCurrency : loggedUserCurrency,
                t('commissions.summary.personal'),
                isCrypto ? revenue : userPrices?.revenue?.price,
              )}
              {!isCrypto && revenue && netRevenue
                ? summaryContent(
                    isCrypto,
                    isCrypto ? cryptoCurrency : loggedUserCurrency,
                    t('commissions.summary.memberShip'),
                    revenue - netRevenue,
                    isCrypto
                      ? revenue - netRevenue
                      : userPrices?.revenue?.price -
                          userPrices?.netRevenue?.price,
                  )
                : null}
              {summaryContent(
                isCrypto,
                isCrypto ? cryptoCurrency : loggedUserCurrency,
                t('commissions.summary.tax'),
                isCrypto ? revenueTax : userPrices?.revenueTax?.price,
                'tax',
                tax,
              )}
              {summaryContent(
                isCrypto,
                isCrypto ? cryptoCurrency : loggedUserCurrency,
                t('commissions.summary.totalCommissions'),
                isCrypto ? revenueWithVAT : userPrices?.revenueWithVAT?.price,
                'totalCommissions',
              )}
            </div>
          )}
          {error && (
            <div className="row summary-report-commissions">
              <div className="row col-lg-6 col-12">
                <p className="col-6 m--font-danger">
                  {t('error_reportNotFound')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SponsorCommissionList.propTypes = {
  t: PropTypes.func.isRequired,
  onCommissionReport: PropTypes.func.isRequired,
  isRecurrenceInterval: PropTypes.bool,
  onListInitialization: PropTypes.func.isRequired,
  tableId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    sponsor: PropTypes.string,
    saleStatus: PropTypes.string,
  }).isRequired,
  isWeelodgeCommissionModel: PropTypes.bool.isRequired,
  directSellingSettings: PropTypes.shape({
    VAIntegration: PropTypes.bool,
    salesIntegration: PropTypes.bool,
  }),
  summary: PropTypes.shape({
    show: PropTypes.bool,
    error: PropTypes.bool,
    revenue: PropTypes.number,
    netRevenue: PropTypes.number,
    revenueTax: PropTypes.number,
    tax: PropTypes.number,
    revenueWithVAT: PropTypes.number,
    userPrices: PropTypes.shape({
      revenue: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
      netRevenue: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
      revenueTax: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
      revenueWithVAT: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
      commission: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
      amount: PropTypes.shape({
        price: PropTypes.number,
        code: PropTypes.string,
      }),
    }),
  }),
  currency: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  activeWallet: PropTypes.shape(WalletProp.propType),
};

SponsorCommissionList.defaultProps = {
  directSellingSettings: {
    salesIntegration: false,
    VAIntegration: false,
  },
  summary: {
    show: false,
    error: false,
    revenue: null,
    netRevenue: null,
    revenueTax: null,
    tax: null,
    revenueWithVAT: null,
    userPrices: null,
  },
  activeWallet: null,
  isRecurrenceInterval: false,
};

export default translate(SponsorCommissionList);
